/**
 * Navigation Components
 * A responsive navigation bar with dynamic routing, dropdown menus, and authentication state management
 * Features collection management, admin controls, and user authentication status
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useMutation } from "react-query";
import useAuthHook from "./api/auth";
import { useNavigate, useLocation } from "react-router-dom";
import useAuth from "../../hooks/use-auth";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { useSelector } from "react-redux";
import axios from "axios";

/**
 * Navbar Component
 * Main navigation component providing routing controls and user interface elements
 *
 * Features:
 * - Dynamic route handling and active state management
 * - Collections dropdown with dynamic content
 * - Authentication state display and logout functionality
 * - Responsive design with mobile considerations
 *
 * @returns {JSX.Element} A navigation bar with dynamic routing and authentication controls
 */
export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { postLogout } = useAuthHook();
  const { auth } = useAuth();
  const config = useSelector((state) => state.config);
  const [collections, setCollections] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [wallet, setWallet] = useState();

  /**
   * Fetches collection data from the server
   * @param {string[]} collectionList - Array of collection addresses to fetch
   * @returns {Promise<Array>} Collection data from the server
   */
  const getCollections = useCallback(async (collectionList) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/collections-claim-ids/multiple`,
        { addresses: collectionList },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching collections:", error);
      return [];
    }
  }, []);

  /**
   * Effect hook to fetch collections when config changes
   * Manages collection data retrieval and state updates
   */
  useEffect(() => {
    const fetchCollections = async () => {
      if (config?.clientConfig?.data?.collections) {
        const data = await getCollections(config.clientConfig.data.collections);
        setCollections(data);
      }
    };

    const getWalletData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/api/wallet`,
        );
        console.log("response.data", response.data);
        setWallet(response.data);
      } catch (error) {
        console.error("Error fetching wallet data:", error);
      }
    };

    getWalletData();

    fetchCollections();
  }, [config, getCollections]);

  /**
   * Effect hook to handle clicks outside the dropdown menu
   * Closes dropdown when clicking outside its boundaries
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /**
   * Mutation hook for handling logout functionality
   * Navigates to login page on successful logout
   */
  const { mutateAsync } = useMutation(postLogout, {
    onSuccess: () => navigate("/login"),
  });

  /**
   * Handles user logout action
   * Initiates logout mutation when called
   */
  const logoutHandler = () => mutateAsync({});

  /**
   * Toggles dropdown menu visibility
   */
  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

  /**
   * Checks if the current path matches the target path
   * @param {string} path - Target path to check against current location
   * @returns {boolean} Whether the path is currently active
   */
  const isActivePath = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }

    if (path === "/admin") {
      return location.pathname === "/admin";
    }

    return location.pathname.startsWith(path) && path !== "/admin";
  };

  /**
   * Generates CSS classes for navigation buttons based on active state
   * @param {string} path - Path to generate classes for
   * @returns {string} CSS classes for the button
   */
  const getButtonClasses = (path) => {
    return `px-3 py-2 ${
      isActivePath(path)
        ? "text-blue-700 font-semibold border-b-2 border-blue-700"
        : "text-gray-700 hover:text-blue-600"
    }`;
  };

  return (
    <nav className="bg-white shadow">
      <div className="mx-auto px-4">
        <div className="flex h-16 justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate("/")}
              className={getButtonClasses("/")}
            >
              Vai alla piattaforma
            </button>

            <button
              onClick={() => navigate("/admin")}
              className={getButtonClasses("/admin")}
            >
              Dashboard
            </button>

            <button
              onClick={() => navigate("/admin/new-collection")}
              className={getButtonClasses("/admin/new-collection")}
            >
              Crea Collezione
            </button>

            <div className="relative inline-block" ref={dropdownRef}>
              <button
                onClick={toggleDropdown}
                className={`inline-flex items-center ${getButtonClasses("/admin/claim-ids")}`}
              >
                <span>Claim Ids</span>
                {collections?.data?.length > 0 && (
                  <ChevronDownIcon
                    className={`ml-2 h-5 w-5 transition-transform duration-200 ${
                      isDropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                )}
              </button>
              {isDropdownOpen && collections?.data?.length > 0 && (
                <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10">
                  {collections?.data.map((collection) => (
                    <button
                      key={collection._id}
                      onClick={() => {
                        setIsDropdownOpen(false);
                        navigate(`/admin/claim-ids/${collection.address}`);
                      }}
                      className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 truncate overflow-hidden"
                    >
                      {collection.collectionName}
                    </button>
                  ))}
                </div>
              )}
              {isDropdownOpen &&
                config?.clientConfig?.data?.collections?.length === 0 && (
                  <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg p-4">
                    No collections found
                  </div>
                )}
            </div>

            <button
              onClick={() => navigate("/admin/reports")}
              className={getButtonClasses("/admin/reports")}
            >
              Segnalazioni
            </button>

            <button
              onClick={() => navigate("/admin/settings")}
              className={getButtonClasses("/admin/settings")}
            >
              Impostazioni
            </button>
          </div>

          <div className="flex items-center space-x-4">
            <div>Balance: {parseFloat(wallet?.balance).toFixed(4)}</div>
            <div>{auth.userInfo.email}</div>
            <button
              onClick={logoutHandler}
              className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-md"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
